import { t } from 'i18next';

import { HOME_ROUTE } from '../constants/auth';
import { DATA_SIZE_BASE, SI_PREFIXES } from '../constants/constants';
import i18n from '../translations/i18n';
import { GenericObject } from '../types/genericTypes';

export const toLocale = (lokaliseISOCode: string): string => {
  const locale = lokaliseISOCode?.replace('_', '-').toLowerCase() ?? 'en';
  return locale === 'es' ? 'es-CL' : locale;
};

export const toLocaleDateArray = (dates: string[], dateType: 'short' | 'long' = 'short'): string[] => {
  return dates.map((d) => {
    const [year, month, day] = d.split('-');
    return capitalize(
      new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
        .toLocaleString(toLocale(i18n.resolvedLanguage ?? 'es'), { month: dateType })
        .replace('.', '')
    );
  });
};

export const toLocalDateString = (
  dateString: string,
  includeYear = false,
  monthType: 'short' | 'long' | 'numeric' | '2-digit' | 'narrow' | undefined = 'long'
): string => {
  const dateStringArray = dateString.split('-');
  const year = dateStringArray[0];
  const month = capitalize(
    new Date(
      parseInt(dateStringArray[0]),
      parseInt(dateStringArray[1]) - 1,
      parseInt(dateStringArray[2])
    ).toLocaleString(toLocale(i18n.resolvedLanguage ?? 'es'), {
      month: monthType
    })
  );
  return includeYear ? `${month} ${year}` : month;
};

export const toLocaleNumberFormat = (
  number: number,
  options: Intl.NumberFormatOptions = {},
  locale: string = toLocale(i18n.resolvedLanguage ?? 'es')
): string => {
  try {
    if (Object.keys(options).length !== 0) {
      return new Intl.NumberFormat(locale, options).format(number);
    }
    return new Intl.NumberFormat(locale).format(number);
  } catch {
    return `${number} ${options.currency ?? ''}`;
  }
};

export const getUsernameFromEmail = (email: string): string => {
  return email.split('@')[0];
};

export const downloadFile = (url: string, newTab = false): void => {
  const link = document.createElement('a');
  link.href = url;
  link.target = newTab ? '_blank' : '_self';
  link.setAttribute('download', '');
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const capitalize = (word: string): string => {
  return word?.toString().replace(/^\w/, (c) => c.toUpperCase());
};

export const toTitleCase = (words: string): string => {
  return words.replace(/\w\S*/g, function (word) {
    return word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
  });
};

export const getInitialsLetters = (fullName: string, splitBy = ', '): string => {
  const initialLettersArray = fullName.split(splitBy).map((name) => name[0]);
  return initialLettersArray.join('').toLocaleUpperCase();
};

export const invertDateFormat = (
  date: string,
  separator = '-',
  newSeparator = '/',
  includeYear = true,
  removeTime = false
) => {
  if (removeTime) {
    date = date.split('T')[0];
  }
  const dateArray = date.split(separator).reverse();
  return includeYear ? dateArray.join(newSeparator) : dateArray.slice(0, 2).join(newSeparator);
};

export const validateFileList = (
  fileList: FileList,
  count: number,
  formats: string[],
  maxSize: number
): { invalidCount: boolean; invalidFormat: number[]; invalidSize: boolean } => {
  const filesList = [...fileList];
  const invalidCount = count < filesList.length;
  const invalidFormat = filesList.reduce<number[]>(
    (list, file, i) => (!validateFileFormat(file, formats) ? [...list, i] : list),
    []
  );
  const invalidSize = filesList.some((file) => file.size > maxSize);
  return { invalidCount, invalidFormat, invalidSize };
};

export const validateFileFormat = (file: File, formats: string[]): boolean => {
  return formats.some((format) => file.name.toLowerCase().endsWith(format.toLowerCase()));
};

export const textList = (list: string[], separator: string, lastSeparator = separator): string => {
  return `${list.slice(0, -1).join(separator)}${lastSeparator}${list.slice(-1)}`;
};

export const bytesToSize = (bytes: number, decimals = 2, type: 'binary' | 'decimal' = 'binary'): string => {
  const base = DATA_SIZE_BASE[type];
  const exponent = Math.floor(Math.log(bytes) / Math.log(base));
  const fixesSize = parseFloat((bytes / Math.pow(base, exponent)).toFixed(decimals));
  const unit = `${SI_PREFIXES[exponent]}${type === 'binary' ? 'i' : ''}B`;
  return `${fixesSize} ${unit}`;
};

export const fileToBase64 = (file: File) => {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const base64 = reader.result?.toString().split(',')[1];
      resolve(base64 ?? '');
    };
    reader.onerror = () => {
      const errorMessage = reader.error?.message ?? 'Unknown error occurred while reading the file';
      reject(new Error(errorMessage));
    };
  });
};

export const redirectToPage = (navigate: any, redirectTo = HOME_ROUTE, target = '') => {
  redirectTo.includes('https') ? window.open(redirectTo, target) : navigate(redirectTo);
};

export const formatMonthWithMM = (date: Date) => {
  const startDate = new Date(date.getFullYear(), date.getMonth(), 1);
  const startedDate = startDate.toISOString().split('T')[0];

  startDate.setMonth(startDate.getMonth() + 1);
  const finishedDate = startDate.toISOString().split('T')[0];

  return [startedDate, finishedDate];
};

export const returnYYYYMMDD = (date: Date) => {
  return date.getFullYear() + '-' + ('0' + (date.getMonth() + 1)).slice(-2) + '-' + ('0' + date.getDate()).slice(-2);
};

export const getMonthName = (inputDateString: string) => {
  const [year, month, day] = inputDateString.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day);

  const currentYear = new Date().getFullYear();
  const monthName = inputDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();

  if (year < currentYear) {
    const monthTranslation = t(`csp.months.${monthName}`);
    return `${monthTranslation} ${year}`;
  } else {
    return t(`csp.months.${monthName}`);
  }
};

export const getLocalizedMonthName = (monthNumber: number, locale = 'en-US') => {
  const currentYear = new Date().getFullYear();
  const monthName = new Intl.DateTimeFormat(locale, { month: 'long' })
    .format(new Date(currentYear, monthNumber - 1))
    .toLowerCase();
  return t(`csp.months.${monthName}`);
};

export const toLocalStorage = (name: string, value: string) => {
  localStorage.setItem(name, value);
};

export const getFromLocalStorage = (name: string) => {
  return localStorage.getItem(name);
};

export const removeFromLocalStorage = (name: string) => {
  localStorage.removeItem(name);
};

export const getAndRemoveLocalStorage = (name: string) => {
  const item = getFromLocalStorage(name);
  removeFromLocalStorage(name);
  return item;
};

export const isStringWithValue = (value: string | undefined | null): boolean => {
  return value !== undefined && value !== null && value !== '';
};

export const limitDecimals = (number: number, decimals = 2): number => {
  return parseFloat(number.toFixed(decimals));
};

export const base64ToFile = (base64: string, contentType: string, fileName: string) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: contentType });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const sortArray = (arr: GenericObject[], columnKey: string, isAscending: boolean) => {
  return arr.sort((a, b) => {
    if (a[columnKey] < b[columnKey]) {
      return isAscending ? -1 : 1;
    } else if (a[columnKey] > b[columnKey]) {
      return isAscending ? 1 : -1;
    } else {
      return 0;
    }
  });
};

export const parseFormattedNumber = (input: string) => {
  return Number(input.replace(/\./g, ''));
};

export const isDateAfterToday = (date: string, currentDate?: string): boolean => {
  const [year, month, day] = date.split('-').map(Number);
  const inputDate = new Date(year, month - 1, day);
  const today = currentDate ? new Date(currentDate) : new Date();

  inputDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return today < inputDate;
};

export const objectHasKey = <T extends object>(key: PropertyKey, obj: T): key is keyof T => {
  return key in obj;
};

export const formatPercentage = (value: number) => {
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded.toFixed(0)}%` : `${rounded.toFixed(1)}%`;
};
